/* lib/persistence.js — backend state fetch/put, normalize(), the localStorage
 * mirror and the asset uploader for the Catalog Technician workspace.
 *
 * ┌─ SCHEMA CONTRACT (cat-arch.md §0 — BINDING) ────────────────────────────┐
 * │ The mirror key "lhc-catalog-v1" is ALWAYS written with _v:2 — the LIVE  │
 * │ /studio engine (lhc-app.html:58944/59052) and the backend PUT           │
 * │ (server.py:1759) hard-require 2. The prototype's schema-4 semantics     │
 * │ (carcass seed-merge, oversize/supplier defaults) run IN-MEMORY inside   │
 * │ normalize() only; the version byte on disk/wire never changes.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Endpoints (cat-integ.md §1 — byte-compatible with the old CatalogTechApp):
 *   GET  /api/catalog/state                    → { tenant, blob, updated_at }
 *   PUT  /api/catalog/state       body {blob}  (debounced 800ms per edit)
 *   POST /api/admin/catalog/assets/upload      multipart {file} → {url}
 * Auth rides on the global axios Bearer default set by AuthContext. */
import axios from "axios";
import { stampUids } from "../../configurator/engine/catalogIds";
import { writeCatalogMirror, ownerKeyFor } from "../../lib/catalogMirror";
import { freshSeeds, EQ_CATS } from "../data";
import { unitHasDrawers, canonicalizeDims, dimsFromSizeMm, dimsMismatchMm } from "./units";
import { adoptLegacy } from "./entities";
import { cfgOf, prodOpts } from "./pricing";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/* ── backend I/O ─────────────────────────────────────────────────────────── */
export async function fetchState() {
  const { data } = await axios.get(`${API}/catalog/state`);
  return data;
}

export async function putState(blob) {
  await axios.put(`${API}/catalog/state`, { blob });
}

/** Upload a GLB/texture → ABSOLUTE url (cross-origin iframe-safe). NEVER
 *  persist base64 models into the blob — store this URL on u.model. */
export async function uploadAsset(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/admin/catalog/assets/upload`, fd);
  return BACKEND + data.url;
}

/* ── DATA-URL TEXTURE SWEEPER (Stage 4 hygiene, 2026-08-04) ────────────────
 * Historically every technician texture (door/carcass/gola/plinth/fabric
 * swatches, entity thumbs, the melamine generator) was stored as a base64
 * data-URL INSIDE the blob — pushing the 16MB Mongo document ceiling and the
 * ~5MB localStorage mirror. The sweeper (CatalogTechApp effect) finds every
 * embedded image and moves it to /uploads, storing the URL instead. Purely
 * self-healing: runs whenever data-URLs exist, nothing to migrate = no-op. */

/** collectDataUrlTexRefs(blob) → [{ path: [keys…], len }] for every string
 *  value that IS an embedded image (data:image/…). Pure — used by the
 *  sweeper and pinned by tests. */
export function collectDataUrlTexRefs(node, base = [], out = []) {
  if (!node || typeof node !== "object") return out;
  const keys = Array.isArray(node) ? node.map((_, i) => i) : Object.keys(node);
  for (const k of keys) {
    const v = node[k];
    if (typeof v === "string") {
      if (v.indexOf("data:image/") === 0 && v.length > 256) out.push({ path: base.concat([k]), len: v.length });
    } else if (v && typeof v === "object") {
      collectDataUrlTexRefs(v, base.concat([k]), out);
    }
  }
  return out;
}

/** uploadDataUrlAsset(durl) → /uploads URL for an embedded image. */
export async function uploadDataUrlAsset(durl) {
  const blob = await (await fetch(durl)).blob();
  const ext = ((blob.type || "image/jpeg").split("/")[1] || "jpg").replace("jpeg", "jpg");
  const f = new File([blob], "tex-migrated." + ext, { type: blob.type || "image/jpeg" });
  return uploadAsset(f);
}

/* ── mirror (the /studio engine reads this key at boot) ─────────────────── */
export function writeMirror(blob, user) {
  // writeCatalogMirror spreads the blob and force-sets _v:2 + the owner tag.
  writeCatalogMirror(blob, ownerKeyFor(user));
}

/* ── empty blob shape (every field the store contract carries) ──────────── */
export function emptyBlob() {
  return {
    groups: [], handles: [], gola: [], carcass: [], appliances: [], colors: [],
    plinth: [], pmat: [], dstyles: [], countertops: [],
    lighting: { transformer: 60, sensor: 25, drawer: { label: "Drawer Lighting", note: "", sizes: [] }, vertical: { pricePerM: 0 }, horizontal: { pricePerM: 0 } },
    fabrics: { indoor: [], leather: [], outdoor: [], rope: [] },
    worktops: [], backsplash: [],
    equipment: EQ_CATS.reduce((o, c) => { o[c] = []; return o; }, {}),
    pgCats: [],
    collArt: {},   // collection presentation artwork: { "<family>|<collection>": url }
  };
}

/* ── normalize(): saved tenant blob + seeds → full in-memory blob ────────
 * Ports (in-memory only, no version gate — the backend already scopes us):
 *   • loadCatalog (HTML 53855-53868): groups merged onto seed by g; carcass
 *     replaced by saved list with missing seed SKUs RE-APPENDED (schema-4
 *     semantics); other entity arrays replaced when present.
 *   • ensureCatalogShape (53848): groups oversize default {1000,12}; items
 *     supplier default "In-house".
 *   • ensureLightingDefaults (53846-47): u.opts.lighting defaults by drawers.
 *   • normalizePlinth (53797): PVC → "Coated Plastic Plinths", "X Matt" →
 *     "Matt X" colour renames.
 *   • cfgOf pre-create: legacy tenant blobs lack u.cfg — created here so the
 *     render path never mutates. */
export function normalize(saved) {
  const seeds = freshSeeds();
  const d = (saved && typeof saved === "object") ? saved : {};
  const blob = emptyBlob();

  // groups: saved fields win per-group (price/oversize/doors like the
  // prototype, plus the React-side extras name/finish/label/edgePrice the old
  // workspace persisted); saved groups unknown to the seed are appended.
  blob.groups = seeds.groups;
  if (Array.isArray(d.groups)) {
    d.groups.forEach((sg) => {
      if (!sg || sg.g == null) return;
      const i = blob.groups.findIndex((g) => g.g === sg.g);
      if (i >= 0) blob.groups[i] = { ...blob.groups[i], ...clone(sg) };
      else blob.groups.push(clone(sg));
    });
  }

  // carcass: saved list wins; seed SKUs missing from the save are re-appended
  // (the schema-4 merge rule — keeps newly-shipped SKUs alive) — UNLESS the
  // technician DELETED them. Deletions are recorded as tombstones in
  // blob.delSeedSkus (2026-08-06 — without them every deleted seed product
  // resurrected on refresh). The tombstone only gates this AUTO re-append:
  // re-adding/importing a SKU simply puts it back in the saved list, which
  // always wins.
  const tomb = {};
  (Array.isArray(d.delSeedSkus) ? d.delSeedSkus : []).forEach((s) => { if (typeof s === "string" && s) tomb[s] = 1; });
  if (Array.isArray(d.carcass)) {
    const list = clone(d.carcass);
    const have = {};
    list.forEach((u) => { if (u && u.sku) have[u.sku] = 1; });
    seeds.carcass.forEach((u) => { if (u && u.sku && !have[u.sku] && !tomb[u.sku]) list.push(u); });
    blob.carcass = list;
  } else {
    blob.carcass = seeds.carcass;
  }
  blob.delSeedSkus = Array.isArray(d.delSeedSkus) ? clone(d.delSeedSkus) : [];

  // simple entity arrays: saved replaces seed when present (prototype
  // splice-replace), otherwise the seed ships.
  for (const k of ["handles", "gola", "appliances", "colors", "plinth", "pmat", "dstyles", "countertops", "worktops", "backsplash"]) {
    blob[k] = Array.isArray(d[k]) ? clone(d[k]) : seeds[k];
  }
  // SIDE PANELS (2026-08-29): decorative end panels — no seed, starts empty.
  blob.sidePanels = Array.isArray(d.sidePanels) ? clone(d.sidePanels) : [];
  // …and their shared colour pool (2026-08-29d), the Gola Colours twin.
  blob.sidePanelColors = Array.isArray(d.sidePanelColors) ? clone(d.sidePanelColors) : [];

  // lighting: object merge with the prototype's lightingV3 defaults IIFE
  // (+ v4.56 sensor surcharge default).
  const L = (d.lighting && typeof d.lighting === "object") ? clone(d.lighting) : seeds.lighting;
  if (typeof L.transformer !== "number") L.transformer = seeds.lighting.transformer;
  if (typeof L.sensor !== "number") L.sensor = (typeof seeds.lighting.sensor === "number" ? seeds.lighting.sensor : 25);
  if (!L.drawer || !Array.isArray(L.drawer.sizes)) L.drawer = seeds.lighting.drawer;
  if (!L.vertical) L.vertical = seeds.lighting.vertical;
  if (!L.horizontal) L.horizontal = seeds.lighting.horizontal;
  blob.lighting = L;

  // fabrics: zone object; each zone falls back to its seed when absent.
  const F = (d.fabrics && typeof d.fabrics === "object") ? clone(d.fabrics) : {};
  for (const z of ["indoor", "leather", "outdoor", "rope"]) {
    if (!Array.isArray(F[z])) F[z] = seeds.fabrics[z] || [];
  }
  blob.fabrics = F;

  // equipment: 15 fixed categories (v4.56 adds bins/corners/pullouts/columns/
  // organizers/hinges/drawers/shelves); missing ones fall back to seed.
  const E = (d.equipment && typeof d.equipment === "object") ? clone(d.equipment) : {};
  for (const c of EQ_CATS) {
    if (!Array.isArray(E[c])) E[c] = seeds.equipment[c] || [];
  }
  blob.equipment = E;

  blob.pgCats = (Array.isArray(d.pgCats) && d.pgCats.length) ? clone(d.pgCats) : seeds.pgCats;

  // collection presentation artwork (2026-07-25 single-source: the technician
  // owns the model-selector card imagery; keyed "<family>|<collection>")
  blob.collArt = (d.collArt && typeof d.collArt === "object") ? clone(d.collArt) : {};

  // PG1 melamine auto-texture guard (AccessoriesView) — carried through so the
  // once-per-blob side effect stays durable across reloads.
  if (d.pg1MelDone) blob.pg1MelDone = true;

  // ── v4.56 one-shot migrations (prototype golaMigV2 / aluPlinthMigV1) ────
  // Flags live IN the blob so they persist with the next PUT; until then the
  // migration is deterministic and re-runs identically at each boot.
  if (d.golaMigV2) blob.golaMigV2 = true;
  if (!blob.golaMigV2) {
    blob.gola = [
      { name: "Matt Black", sw: "#292B30", finish: "Anodised", price: 4 },
      { name: "Champagne", sw: "#E3E4E6", finish: "Anodised", price: 0 },
      { name: "Brushed Brass", sw: "#7A7B80", finish: "Coated", price: 8 },
      { name: "Matt Black · Lighting", sw: "#292B30", finish: "LED integrated · warm white", price: 42 },
      { name: "Champagne · Lighting", sw: "#E3E4E6", finish: "LED integrated · warm white", price: 38 },
      { name: "Brushed Brass · Lighting", sw: "#7A7B80", finish: "LED integrated · warm white", price: 46 },
    ];
    blob.golaMigV2 = true;
  }
  if (d.aluPlinthMigV1) blob.aluPlinthMigV1 = true;
  if (!blob.aluPlinthMigV1) {
    for (const m of blob.pmat) {
      if (m && m.key === "aluminum") {
        m.colors = [
          { name: "Natural anodised · 100mm", sw: "#9A9BA0" },
          { name: "Black anodised · 100mm", sw: "#292B30" },
          { name: "Champagne · 100mm", sw: "#9A9BA0" },
          { name: "Natural anodised · Lighting", sw: "#9A9BA0" },
          { name: "Black anodised · Lighting", sw: "#292B30" },
          { name: "Champagne · Lighting", sw: "#9A9BA0" },
        ];
        break;
      }
    }
    blob.aluPlinthMigV1 = true;
  }
  /* ── pubSplitV1 (2026-08-04 publication split) ──────────────────────────
   * The publication predicate became STRICT: published = inCatalog AND
   * status === "live" (catalogBridge.isUnitPublished, JS ≡ PY). Under the
   * old lenient rule an inCatalog unit with an implicit status ("np" /
   * "priced" / missing — anything but "off") was published; stamp those
   * "live" ONCE so nothing that was published silently unpublishes. New
   * Add-to-Catalog arrives status "off", which this latch never touches. */
  if (d.pubSplitV1) blob.pubSplitV1 = true;
  if (!blob.pubSplitV1) {
    blob.carcass.forEach((u) => {
      if (u && u.inCatalog && u.status !== "off" && u.status !== "live") u.status = "live";
    });
    blob.pubSplitV1 = true;
  }

  // ── in-memory schema-4 shape passes ────────────────────────────────────
  // ensureCatalogShape (oversize + supplier defaults)
  blob.groups.forEach((g) => { if (!g.oversize) g.oversize = { overMm: 1000, addPct: 12 }; });
  blob.carcass.forEach((u) => { if (u && u.supplier === undefined) u.supplier = "In-house"; });
  // ensureLightingDefaults + cfg pre-create
  blob.carcass.forEach((u) => {
    if (!u) return;
    const o = prodOpts(u);
    if (!Array.isArray(o.lighting)) o.lighting = unitHasDrawers(u) ? ["drawer"] : ["vertical", "horizontal"];
    cfgOf(u);
  });
  normalizePlinth(blob.pmat);

  /* ── MOJIBAKE REPAIR (2026-08-06, one-shot) — the CSV importer used to
   * decode UTF-8 files as latin-1 (usability-audit A15), storing `·` as `Â·`,
   * `×` as `Ã—`, `€` as `â‚¬` inside product names/descriptions. The decode
   * is fixed at the source (lib/imports.js); this heals what was already
   * stored. decodeURIComponent(escape(s)) is the exact inverse of a latin-1
   * mis-decode of UTF-8 bytes; applied ONLY to strings that visibly carry a
   * mojibake signature, and only when the reverse decode succeeds. */
  if (d.mojibakeFixV1) blob.mojibakeFixV1 = true;
  if (!blob.mojibakeFixV1) {
    const MOJI = /Â|Ã[—©¨¼¶¤±-¿]|â‚¬|â€/;
    // the corruption can be stored two ways: raw latin-1 control chars (what
    // SheetJS wrote) or their Windows-1252 DISPLAY forms (€ ‚ — … etc. — what
    // a copy/paste round-trip produces). Map the display forms back to their
    // byte values first, then reverse the latin-1 mis-decode.
    const CP1252 = { "€": 0x80, "‚": 0x82, "ƒ": 0x83, "„": 0x84, "…": 0x85, "†": 0x86, "‡": 0x87, "ˆ": 0x88, "‰": 0x89, "Š": 0x8a, "‹": 0x8b, "Œ": 0x8c, "Ž": 0x8e, "‘": 0x91, "’": 0x92, "“": 0x93, "”": 0x94, "•": 0x95, "–": 0x96, "—": 0x97, "˜": 0x98, "™": 0x99, "š": 0x9a, "›": 0x9b, "œ": 0x9c, "ž": 0x9e, "Ÿ": 0x9f };
    const heal = (s) => {
      if (typeof s !== "string" || !MOJI.test(s)) return s;
      try {
        const pre = s.replace(/[€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]/g, (ch) => String.fromCharCode(CP1252[ch]));
        // eslint-disable-next-line no-restricted-globals
        const fixed = decodeURIComponent(escape(pre));
        return MOJI.test(fixed) ? s : fixed;         // never trade one corruption for another
      } catch (e) { return s; }
    };
    blob.carcass.forEach((u) => {
      if (!u) return;
      ["name", "desc", "dims", "mat", "cat", "sub", "group"].forEach((k) => {
        if (u[k] != null) u[k] = heal(u[k]);
      });
    });
    blob.mojibakeFixV1 = true;
  }

  /* ── DIMS CANONICALIZATION (2026-08-09, one-shot) — imported dims strings
   * mixed cm and mm ("300x120x350"), and the per-value mm tolerance (≥320)
   * cannot represent mm values under 320: the technician read the SKU width
   * (300mm) while the configurator bridge read the dims width as 300 CM — a
   * 3-metre wall unit. Re-express every parseable dims string in the
   * canonical CM form (see lib/units.canonicalizeDims), which BOTH parsers
   * read identically. Preserves the technician's current interpretation
   * exactly; idempotent; units without 3 numbers are never touched. */
  if (d.dimsCanonV1) blob.dimsCanonV1 = true;
  if (!blob.dimsCanonV1) {
    blob.carcass.forEach((u) => {
      if (!u) return;
      const canon = canonicalizeDims(u);
      if (canon && canon !== u.dims) u.dims = canon;
    });
    blob.dimsCanonV1 = true;
  }

  /* ── THE UPLOADED MODEL IS THE TRUTH (2026-08-24, one-shot) — user report:
   * "the sizes of modules uploaded in Cabinet Technician aren't the same as
   * the uploaded GLB". Two ways a row drifted: (a) DUPLICATING a product
   * copies its dims, and attaching a different .glb never touched them (a
   * 2201mm-tall model stored as 780mm — the configurator then squashed the
   * model into the wrong box, because the GLB is fitted to the def dims);
   * (b) an imported mm string under the 320 mm-tolerance ("300x120x35")
   * canonicalized as 300 CM — a 3-metre wall unit.
   * A product WITH a parsed model size now takes its dims FROM the model
   * (>5mm on any axis = dimsMismatchMm). The previous string is kept on
   * u.dimsPrevGlbFix so nothing is lost and the change is auditable. Rows
   * without a model are never touched (no ground truth to prefer), and the
   * technician can still edit any dims by hand afterwards. */
  if (d.dimsFromGlbV1) blob.dimsFromGlbV1 = true;
  if (!blob.dimsFromGlbV1) {
    blob.carcass.forEach((u) => {
      if (!u || !u.model3dMeta) return;
      if (!dimsMismatchMm(u)) return;                 // matches (or no size) → leave alone
      const canon = dimsFromSizeMm(u.model3dMeta);
      if (!canon || canon === u.dims) return;
      if (u.dims) u.dimsPrevGlbFix = u.dims;
      u.dims = canon;
      blob.__dimsHealed = true;                       // store.init persists once (stripped there)
    });
    blob.dimsFromGlbV1 = true;
  }

  /* ── LOSSLESS PASS (2026-07-31) — normalize used to build the blob from an
   * ALLOW-LIST of known keys, so every key it did not name was silently
   * DROPPED on the next save. That quietly wiped technician-authored data the
   * rest of the platform depends on: the shared component library
   * (blob.compLib), collections (blob.collections) and the pricing block
   * (blob.pricing — cut fee, lead times). The technician is the master
   * database: anything stored on the blob must survive a round-trip, including
   * keys added by features normalize predates. Known keys keep their curated
   * handling above; everything else is preserved verbatim. ───────────────── */
  Object.keys(d).forEach((k) => {
    // __dimsHealed is a TRANSIENT signal for store.init (persist the heal
    // once) — never data, so it must not ride the lossless pass back in.
    if (k === "__dimsHealed") return;
    if (blob[k] === undefined) blob[k] = clone(d[k]);
  });

  /* ── MATERIAL LIBRARY (Stage 1, 2026-08-04) — blob.materials[] holds the
   * technician's material RECORDS (texture-map URLs + params + tileMm; the
   * binaries live under /uploads, NEVER as data-URLs in the blob). The
   * lossless pass above already preserves it; this just guarantees the array
   * exists so views and the render registry never branch on undefined. */
  if (!Array.isArray(blob.materials)) blob.materials = [];

  /* ── MATERIAL CATEGORIES (2026-08-09 redesign) — the fixed wood/metal/…
   * vocabulary is replaced by TECHNICIAN-CREATED categories:
   * blob.matCats = [{key, label}]. One-shot seed: tenants that already added
   * materials get a category per distinct existing category value, so
   * nothing they organized is orphaned; fresh tenants start empty and create
   * their own. */
  if (!Array.isArray(blob.matCats)) blob.matCats = [];
  if (d.matCatsV1) blob.matCatsV1 = true;
  if (!blob.matCatsV1) {
    const seen = new Set(blob.matCats.map((c) => c && c.key));
    blob.materials.forEach((m) => {
      const k = m && m.category;
      if (typeof k === "string" && k && !seen.has(k)) {
        seen.add(k);
        blob.matCats.push({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1) });
      }
    });
    blob.matCatsV1 = true;
  }

  /* ── SHARED HANDLE COLOURS (2026-08-10 user mandate: "don't add a colour
   * section separately for every handle — ONE Handle Colours section; those
   * colours become available for ALL handles in the configurator").
   * blob.handleColors = [{name, sw, price}]. One-shot merge: existing
   * per-handle colour lists are folded in (dedupe by name, first price wins)
   * and the per-handle arrays removed — one pool, one editor. */
  if (!Array.isArray(blob.handleColors)) blob.handleColors = [];
  if (d.handleColorsV1) blob.handleColorsV1 = true;
  if (!blob.handleColorsV1) {
    const seenHC = new Set(blob.handleColors.map((c) => String((c && c.name) || "").toLowerCase()));
    (blob.handles || []).forEach((h) => {
      if (!h || !Array.isArray(h.colors)) return;
      h.colors.forEach((c) => {
        const k = String((c && c.name) || "").toLowerCase();
        if (!c || !k || seenHC.has(k)) return;
        seenHC.add(k);
        blob.handleColors.push({ name: c.name, sw: c.sw || "#9A9BA0", price: Number(c.price) || 0 });
      });
      delete h.colors;
    });
    blob.handleColorsV1 = true;
  }

  /* ── COMPONENT REGISTRY (Cabinet Technician V2) — adopt the legacy libraries
   * into blob.entities. Runs LAST, after the seed merges and the golaMigV2 /
   * aluPlinthMigV1 latches, so it always adopts the FINAL arrays. Adoption is
   * idempotent and only refreshes the fields that map to a legacy slot, so
   * registry-only data (manufacturer, manufacturing, compatibility, media…)
   * survives, while the legacy editors stay authoritative for what they own. */
  adoptLegacy(blob);

  /* ── STABLE UIDS (2026-08-22 index-id fix): every entry of the positional
   * libraries gets a persistent uid (blob._uidSeq, never re-issued) — the
   * identity every NEW config id ("carc-u7", "gola-u3", …) resolves by, so
   * library deletes/reorders can no longer re-point saved kitchens. Runs
   * after every merge/latch so late-added entries are stamped too. */
  stampUids(blob);

  return blob;
}

/** tombstoneSku(b, sku) — record a product deletion so normalize()'s seed
 *  re-append never resurrects it (call INSIDE the same mutate that splices
 *  the unit out of b.carcass). Idempotent. */
export function tombstoneSku(b, sku) {
  if (!sku) return;
  if (!Array.isArray(b.delSeedSkus)) b.delSeedSkus = [];
  if (b.delSeedSkus.indexOf(sku) < 0) b.delSeedSkus.push(sku);
}

/* normalizePlinth (HTML 53797) — PVC rename + "X Matt" → "Matt X". */
export function normalizePlinth(pmat) {
  try {
    for (let i = 0; i < pmat.length; i++) {
      const m = pmat[i];
      if (!m) continue;
      if (/pvc/i.test((m.name || "") + " " + (m.desc || ""))) {
        m.key = "plastic"; m.name = "Coated Plastic Plinths"; m.desc = "Coated plastic / laminated plinth";
        if (!Array.isArray(m.colors) || !m.colors.length || /anthracite|grey|cream/i.test(JSON.stringify(m.colors))) {
          m.colors = [{ name: "Matt Black", sw: "#1D1F22" }, { name: "Matt White", sw: "#E3E4E6" }, { name: "Matt Champagne", sw: "#9A9BA0" }];
        }
      }
      if (Array.isArray(m.colors)) {
        m.colors.forEach((c) => {
          const mm = /^(.*\S)\s+matt$/i.exec((c.name || "").trim());
          if (mm) c.name = "Matt " + mm[1];
        });
      }
    }
  } catch (e) { /* tolerate malformed pmat */ }
}

/* ── debounced saver + save-state machine ────────────────────────────────
 * onState(next, onlyIfCurrent?) — the store guards the conditional
 * saved → idle transition. Sequence per edit burst:
 *   dirty → (800ms quiet) saving → saved → (1400ms) idle | error
 *
 * SERIALIZED (2026-08-24 — the "clicked live and it reverted" bug): the old
 * saver started a fresh debounce timer per edit with NO in-flight awareness,
 * so an edit made while a PUT was on the wire issued a SECOND overlapping
 * PUT; when the server committed them out of order, the OLDER body won and
 * the newest edit silently unwound (the user's publish click during the
 * Add-to-Catalog save). Now at most ONE PUT is ever in flight — an edit that
 * lands mid-flight is queued (newest wins) and PUT immediately after, so the
 * server always converges to the latest state. putFn is injectable for tests. */
export function createSaver(onState, putFn = putState) {
  let timer = null;
  let inFlight = false;
  let queued = null;                 // newest blob scheduled while a PUT flew
  const flush = async (blob) => {
    inFlight = true;
    onState("saving");
    let cur = blob, failed = false;
    for (;;) {
      try { await putFn(cur); } catch (e) { failed = true; break; }
      if (queued == null) break;     // nothing newer arrived — done
      cur = queued; queued = null;   // an edit landed mid-flight: PUT it now
    }
    inFlight = false;
    if (failed) {
      onState("error");
      // keep the newest un-PUT state alive: re-enter the normal cycle
      if (queued != null) { const nb = queued; queued = null; saver.schedule(nb); }
      return;
    }
    onState("saved");
    setTimeout(() => onState("idle", "saved"), 1400);
  };
  const saver = {
    schedule(blob) {
      onState("dirty");
      if (inFlight) { queued = blob; return; }   // flushed right after the flight
      clearTimeout(timer);
      timer = setTimeout(() => flush(blob), 800);
    },
    cancel() { clearTimeout(timer); queued = null; },
  };
  return saver;
}

function clone(v) { return JSON.parse(JSON.stringify(v)); }
